import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { StatusPill, type StatusTone } from "../ui/StatusPill";
import { CopyField } from "../ui/CopyField";
import { QrCode } from "../ui/QrCode";
import { useCountdown } from "../../lib/useCountdown";
import { useBtcUsdRate } from "../../lib/useBtcUsdRate";
import type { CryptoInvoice } from "./types";

function satsToBtc(sats: number): number {
  return sats / 100_000_000;
}

function formatUsd(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: n < 10 ? 2 : 0 });
}

function describeState(invoice: CryptoInvoice): { tone: StatusTone; label: string } {
  switch (invoice.state) {
    case "created":
      return { tone: "muted", label: "Waiting for payment" };
    case "payment_detected":
      return { tone: "active", label: "Payment detected" };
    case "confirming":
      return {
        tone: "active",
        label: `Confirming (${invoice.latestConfirmations ?? 0}/${invoice.requiredConfirmations ?? 1})`,
      };
    case "paid":
    case "entitlement_granted":
      return { tone: "confirmed", label: "Confirmed" };
    case "expired":
      return { tone: "danger", label: "Expired" };
    case "expired_late_payment":
      return { tone: "danger", label: "Needs manual review" };
    default:
      // Older invoice shape with no `state`, only the legacy `status` field.
      if (invoice.status === "confirmed") return { tone: "confirmed", label: "Confirmed" };
      if (invoice.status === "expired") return { tone: "danger", label: "Expired" };
      return { tone: "muted", label: "Waiting for payment" };
  }
}

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  invoice: CryptoInvoice | null;
  creating: boolean;
  error: string | null;
  /** Re-runs invoice creation — same startInvoice already used for the initial click and "expired" retry. */
  onRetry: () => void;
}

/**
 * Pure presentation for the crypto invoice CryptoPayButton already creates
 * and polls — this component holds no billing state of its own and issues
 * no requests. Closing it only hides the dialog; the polling effect in
 * CryptoPayButton keeps running regardless of `open`.
 */
export function PaymentModal({ open, onClose, invoice, creating, error, onRetry }: PaymentModalProps) {
  const usdRate = useBtcUsdRate();
  const countdown = useCountdown(invoice && invoice.status === "pending" ? invoice.expiresAt : null);

  const isConfirmed = invoice ? describeState(invoice).label === "Confirmed" : false;
  const isLateReview = invoice?.state === "expired_late_payment";
  // Order matters: expired_late_payment is checked above, so this only matches a plain expiry.
  const isExpired = invoice?.state === "expired" || invoice?.status === "expired";

  return (
    <Modal open={open} onClose={onClose} title="Pay with Bitcoin">
      {!invoice && creating && <p className="py-6 text-center text-sm text-muted">Creating invoice…</p>}

      {!invoice && !creating && error && (
        <div>
          <p className="text-sm text-red-400">{error}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {invoice && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <StatusPill tone={describeState(invoice).tone}>{describeState(invoice).label}</StatusPill>
            {invoice.status === "pending" && (
              <p className="text-xs text-muted font-mono">Expires in {countdown.label}</p>
            )}
          </div>

          {isConfirmed ? (
            <p className="text-sm text-signal-400">Payment confirmed — access unlocked. You can close this window.</p>
          ) : isLateReview ? (
            <p className="text-sm text-muted">
              Payment arrived on-chain but only after this invoice expired, so it hasn't been auto-applied.
              Contact support with invoice ID <span className="font-mono text-paper">{invoice.id}</span> for
              manual review.
            </p>
          ) : isExpired ? (
            <div>
              <p className="text-sm text-red-400">This invoice expired before payment was detected.</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
                Generate a new invoice
              </Button>
            </div>
          ) : (
            <>
              <div className="flex justify-center">
                <QrCode value={`bitcoin:${invoice.address}?amount=${satsToBtc(invoice.amountSats).toFixed(8)}`} />
              </div>

              <CopyField label="Send exactly this amount to" value={invoice.address} />

              <div className="flex items-baseline justify-between border-t border-ink-700 pt-3">
                <span className="text-xs text-muted">Amount</span>
                <span className="text-right">
                  <span className="font-mono text-sm">{satsToBtc(invoice.amountSats).toFixed(8)} BTC</span>
                  {usdRate && (
                    <span className="block text-xs text-muted">
                      ≈ {formatUsd(satsToBtc(invoice.amountSats) * usdRate)}
                    </span>
                  )}
                </span>
              </div>

              <a
                href={`bitcoin:${invoice.address}?amount=${satsToBtc(invoice.amountSats).toFixed(8)}`}
                className="block text-center text-sm text-ledger-400 underline"
              >
                Open in wallet app
              </a>

              <p className="text-xs text-muted text-center">
                Checking automatically every few seconds — you can close this window and it'll keep watching.
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
